import React, { useState } from 'react';
import { Layout, Menu, Button, Divider } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  WalletOutlined,
  TagsOutlined,
  PieChartOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { useResponsive } from '../../hooks/useResponsive';
import Logo from './Logo';
import BottomNavBar from './BottomNavBar';
import TopNavBar from './TopNavBar';

const { Sider, Content } = Layout;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = React.memo(({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const { isMobile } = useResponsive();

  console.log('isMobile:', isMobile);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />, label: 'Dashboard',
    },
    {
      key: '/expenses',
      icon: <WalletOutlined />,
      label: 'Quản lý chi tiêu',
      children: [
        { key: '/expenses', label: 'Giao dịch chi tiêu' },
        { key: '/expenses/categories', label: 'Danh mục chi tiêu' },
        { key: '/expenses/reports', label: 'Báo cáo chi tiêu' },
      ],
    },
    {
      key: '/gifts',
      icon: <GiftOutlined />,
      label: 'Quản lý tiền mừng',
      children: [
        { key: '/gifts', label: 'Dashboard' },
        { key: '/gifts/events', label: 'Quản lý sự kiện' },
        { key: '/gifts/givers', label: 'Quản lý người mừng' },
        { key: '/gifts/list', label: 'Danh sách tiền mừng' },
      ],
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Xác định tiêu đề động cho mobile top bar
  const getMobileTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/dashboard')) return 'Dashboard';
    if (path.startsWith('/expenses')) {
      if (path === '/expenses') return 'Giao dịch chi tiêu';
      if (path === '/expenses/categories') return 'Danh mục chi tiêu';
      if (path === '/expenses/reports') return 'Báo cáo chi tiêu';
      return 'Quản lý chi tiêu';
    }
    if (path.startsWith('/income')) {
      if (path === '/income') return 'Giao dịch thu nhập';
      if (path === '/income/wallet') return 'Quản lý ví';
      return 'Quản lý thu nhập';
    }
    if (path.startsWith('/gifts')) {
      if (path === '/gifts') return 'Tiền mừng';
      if (path === '/gifts/events') return 'Quản lý sự kiện';
      if (path === '/gifts/givers') return 'Quản lý khách mời';
      if (path === '/gifts/list') return 'Danh sách tiền mừng';
      return 'Quản lý tiền mừng';
    }
    if (path.startsWith('/transactions')) {
      if (path === '/transactions') return 'Giao dịch';
      return 'Quản lý giao dịch';
    }
    return '';
  };

  return (
    <Layout className="app-layout-root" style={{ 
      minHeight: '100vh', 
      position: 'relative',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      WebkitTouchCallout: 'none',
      WebkitTapHighlightColor: 'transparent'
    }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          width={250}
          theme="dark"
          collapsed={collapsed}
          onCollapse={setCollapsed}
          style={{
            position: 'fixed',
            height: '100vh',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1000,
            transition: 'all 0.3s ease',
            overflowX: 'hidden',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          {/* Logo Section */}
          <div className="logo" style={{ 
            textAlign: 'center', 
            padding: '16px',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <Logo size={collapsed ? 'small' : 'medium'} />
              
              {/* User Info - Only show when not collapsed */}
              {!collapsed && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  flex: 1,
                  minWidth: 0,
                  marginLeft: 8
                }}>
                  <div style={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: '#fff',
                    marginBottom: 4,
                    textAlign: 'left',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>{user?.full_name || user?.username}</div>
                  <div style={{
                    fontSize: 14,
                    color: '#fff',
                    fontWeight: 500,
                    marginBottom: 2,
                    textAlign: 'left',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>{user?.username || 'Người dùng'}</div>
                </div>
              )}
            </div>
          </div>

          {/* Divider between Logo and Menu */}
          <Divider style={{ margin: '0 16px', borderColor: '#303030' }} />

          {/* Menu Section */}
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            style={{ 
              height: 'calc(100vh - 200px)', 
              overflowY: 'auto',
              overflowX: 'hidden',
              textAlign: 'left'
            }}
            className="sidebar-menu"
          />

          {/* User Action Section */}
          <div style={{ 
            position: 'absolute', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            padding: '16px',
            background: '#001529',
            borderTop: '1px solid #303030'
          }}>
            {/* User Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              {/* Logout Button */}
              <Button 
                type="text" 
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{ 
                  flex: 1,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#ff4d4f',
                  background: 'transparent',
                  border: '1px solid #303030',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  height: '36px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 77, 79, 0.1)';
                  e.currentTarget.style.borderColor = '#ff4d4f';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#303030';
                }}
              >
                <span style={{ fontSize: '13px', marginLeft: '8px' }}>Đăng xuất</span>
              </Button>
            </div>
          </div>
        </Sider>
      )}

      {/* Toggle Sidebar Button - Desktop only */}
      {!isMobile && (
        <Button 
          type="text" 
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setCollapsed(!collapsed)}
          style={{ 
            position: 'fixed',
            top: '24px',
            left: collapsed ? '60px' : '230px',
            width: '40px',
            height: '40px',
            color: '#8c8c8c',
            background: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f0f0f0';
            e.currentTarget.style.borderColor = '#1890ff';
            e.currentTarget.style.color = '#1890ff';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.color = '#8c8c8c';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
          }}
        />
      )}

      {/* Main Content */}
      <Layout style={{ 
        marginLeft: !isMobile ? (collapsed ? 80 : 250) : 0,
        paddingBottom: isMobile ? '60px' : 0,
        height: '100vh',
        overflow: 'hidden'
      }}>
        {isMobile && <TopNavBar title={getMobileTitle()} />}
        <Content style={{ 
          margin: isMobile ? '16px' : '24px',
          background: '#f0f2f5',
          borderRadius: '8px',
          height: '100%',
          overflow: 'auto',
          position: 'relative',
          paddingBottom: isMobile ? '20px' : '24px',
          paddingTop: isMobile ? 52 : 0 // Để không bị TopNavBar che
        }}>
          {children}
        </Content>
      </Layout>

      {/* Mobile Bottom Navigation */}
      {isMobile && <BottomNavBar />}
    </Layout>
  );
});

AppLayout.displayName = 'AppLayout';

export default AppLayout; 